import React, { useState, useRef, useEffect, useCallback } from 'react';
import SliderProduct from './SliderProduct';
import { KlevuRecord } from '@klevu/core';

interface SliderProps {
  title?: string;
  products: KlevuRecord[];
  favourites?: string[];
  categoryButtons?: React.ReactNode;
  variant?: 'default' | 'without-offset-margins';
}

export default function Slider({ title, products, favourites = [], categoryButtons, variant = 'default' }: SliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const arrowLeftRef = useRef<HTMLDivElement>(null);
  const arrowRightRef = useRef<HTMLDivElement>(null);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);

  const [showArrows, setShowArrows] = useState(true);
  const [arrowLeftDisabled, setArrowLeftDisabled] = useState(false);
  const [arrowRightDisabled, setArrowRightDisabled] = useState(false);

  const hasHeading = !!title;
  const hasTopContent = hasHeading || categoryButtons;
  
  // Set arrow disabled states based on scroll position
  const setArrowsFromScrollStates = useCallback(() => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    if (slider.scrollWidth - 1 < slider.clientWidth + slider.scrollLeft) {
      setArrowRightDisabled(true);
    } else {
      setArrowRightDisabled(false);
    }
    if (slider.scrollLeft === 0) {
      setArrowLeftDisabled(true);
    } else {
      setArrowLeftDisabled(false);
    }
  }, []);

  // Render arrows based on scrollable content
  const renderArrows = useCallback(() => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    if (slider.scrollWidth > slider.clientWidth) {
      setArrowsFromScrollStates();
    } else {
      setArrowLeftDisabled(true);
      setArrowRightDisabled(true);
    }
  }, [setArrowsFromScrollStates]);

  // Hide/show arrows and scrollbar based on content overflow
  const hideScrollAndArrows = useCallback(() => {
    if (!sliderRef.current || !arrowLeftRef.current || !arrowRightRef.current || !scrollbarTrackRef.current) return;

    const slider = sliderRef.current;
    const cards = slider.querySelectorAll('.productSliderItem');

    if (cards.length === 0) {
      setShowArrows(false);
      return;
    }

    if (slider.scrollWidth <= slider.clientWidth) {
      setShowArrows(false);
    } else {
      setShowArrows(true);
    }
  }, []);

  // Horizontal scroll with smooth animation
  const handleHorizontalScroll = useCallback(
    (direction: 'left' | 'right') => {
      if (!sliderRef.current) return;

      const slider = sliderRef.current;
      let scrollAmount = 0;
      const scrollInterval = setInterval(() => {
        if (direction === 'left') {
          slider.scrollLeft -= 70;
        } else {
          slider.scrollLeft += 70;
        }

        setArrowsFromScrollStates();
        scrollAmount += 70;
        if (scrollAmount >= 500) {
          clearInterval(scrollInterval);
        }
      }, 27);
    },
    [setArrowsFromScrollStates]
  );

  // Handle slider scroll events
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    const handleScroll = () => {
      renderArrows();
      hideScrollAndArrows();
    };

    slider.addEventListener('scroll', handleScroll);
    return () => {
      slider.removeEventListener('scroll', handleScroll);
    };
  }, [renderArrows, hideScrollAndArrows]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      renderArrows();
      hideScrollAndArrows();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [renderArrows, hideScrollAndArrows]);

  // Initialize on mount and when products change
  useEffect(() => {
    renderArrows();
    hideScrollAndArrows();
  }, [renderArrows, hideScrollAndArrows, products]);

  if (!products || products.length === 0) {
    return null;
  }

  const withoutOffsetMargins = variant === 'without-offset-margins';

  return (
    <div className={`thule-container accessory-promotion-container similar-products-slider mb-12 ${withoutOffsetMargins ? 'without-offset-margins' : ''}`}>
      <div className="top-section-wrapper">
        <div className={`top-section mb-6 ${!hasTopContent ? 'no-heading' : ''}`}>
          {hasHeading && <h3 className="heading3">{title}</h3>}
          {categoryButtons}
          {showArrows && (
            <div className="d-none d-lg-flex arrow-container">
              <div
                ref={arrowLeftRef}
                className={`arrow me-4 ${arrowLeftDisabled ? 'disabled' : ''}`}
                onClick={() => handleHorizontalScroll('left')}
              >
                <i className="le-icon-chevron"></i>
              </div>
              <div
                ref={arrowRightRef}
                className={`arrow arrow-right ${arrowRightDisabled ? 'disabled' : ''}`}
                onClick={() => handleHorizontalScroll('right')}
              >
                <i className="le-icon-chevron rot-180"></i>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className="promotion scrollBar--3"
        ref={sliderRef}
      >
        {products.map((product, index) => (
          <SliderProduct key={product.id} product={product} favorites={favourites} position={index + 1} />
        ))}
      </div>
    </div>
  );
}
